import UnlimitedCta from 'components/global/UnlimitedCta'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useAutoScroll } from 'utils/hooks'
import FindTickets from './sections/FindTickets'
import TicketConfiguration from './sections/TicketConfiguration'
import { GuestsDataProvider } from 'components/common/MarchPromo/utils/guestsDataProvider'
import { FamilyPackage } from 'components/global/Family/components/FamilyPackage'
import { FAMILY_PACKAGE_PERSON_COUNT } from 'components/global/Family/constants'
import UnlimitedBirthdayPromotionCta from 'components/global/promotions/BirthdayPromotion/UnlimitedBirthdayPromotionCta'
import Step from 'components/global/Step'
import {
    sumTicketsBundlesDates,
    sumTicketsDates,
} from 'components/global/Summary/helpers'
import UnlimitedBlackCta from 'components/global/UnlimitedBlackCta'
import AddItem from 'components/shared/AddItem'
import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import MarchPromoInfo from 'components/shared/Promotions/components/MarchPromoInfo'
import WinterBreakAttractionsAsideInfo from 'components/shared/Promotions/components/WinterBreakAttractionsAsideInfo'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import Text from 'components/shared/Text'
import { TICKETS_TO_PARK_LIMIT } from 'constants/limits'
import {
    calculateAccommodationAddToCartEvent,
    pushDataLayer,
} from 'helpers/analytics'
import { scrollToTop } from 'helpers/animateScroll'
import { formatDateISO, isDateInRange } from 'helpers/dates'
import { getDarkModeByLanguage } from 'helpers/theme'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import { CREATOR_ACCOMMODATION_STEPS, STEPS } from 'routes/steps'
import { useCart } from 'store/cart-slice/hooks'
import { setIsErrorModalOpened } from 'store/general-slice'
import { useResolutions } from 'store/resolutions'
import { useApp } from 'store/steps'
import {
    addPerson,
    addTicket,
    removePerson,
    setDates,
    setFamilyPackageSelection,
    setFamilyPackageVisibility,
    setMultipleDays,
    setTicketsVisible,
} from 'store/ticket-slice'
import CheckOpeningHours from './CheckOpeningHours'
import { PROMO_NAME_MARCH } from 'components/shared/Promotions/constants'
import { CityBreakBox } from 'components/common/CityBreak/components/CityBreakBox'
import useCityBreak from 'components/common/CityBreak/utils/useCityBreak'

const Tickets = () => {
    const [searchParams] = useSearchParams()
    const dispatch = useAppDispatch()
    const { isTablet } = useResolutions()
    const { isCreatorAccommodation, navigateToStep, stepIndex, language } =
        useApp()
    const { formatMessage } = useIntl()
    const {
        isDarkThemeForTickets,
        isBirthdayPromoForUnlimitedVisibleOutside,
        isBlackPromoForUnlimitedVisibleOutside,
        isWinterBreakPromo,
        getPromotionByName,
    } = usePromotions()
    const { removeNextStepsFromCart, isCartEmpty, isCartFetching } = useCart()
    const { calculateScroll, animatePendingScroll, scrollIndex } =
        useAutoScroll()
    const { isCityBreakApplicableForTickets } = useCityBreak()

    const analyticsRef = useRef(false)

    const {
        people,
        areTicketsVisible,
        ticketBoxes,
        areTicketsUpdating,
        familyPackage,
    } = useAppSelector((state) => state.ticket)

    const { isSelected: isFamilyCheckboxSelected } = familyPackage

    const {
        tickets: cartTickets,
        ticketsBundles,
        ticketsCityBreak,
        accommodation,
        accommodationDetails,
    } = useAppSelector((state) => state.cart.items)
    const { isAssignGuestsVisible } = useAppSelector(
        (state) => state.accommodation
    )

    const [isSkipButtonDisabled, setIsSkipButtonDisabled] = useState(true)

    const areAllTicketsCompleted =
        ticketBoxes.map((ticket) => ticket.ticketTime && ticket.types).flat()
            .length === cartTickets.length

    const isNextStepDisabled =
        ticketBoxes.some((ticket) => !ticket.ticketTime) ||
        isFamilyCheckboxSelected !== !!ticketsBundles.length

    const MobileBenefitsInfoBoxes = useMemo(() => {
        if (isBirthdayPromoForUnlimitedVisibleOutside)
            return <UnlimitedBirthdayPromotionCta />
        if (isBlackPromoForUnlimitedVisibleOutside) return <UnlimitedBlackCta />
        if (isWinterBreakPromo) return <WinterBreakAttractionsAsideInfo />
        return <UnlimitedCta />
    }, [isBlackPromoForUnlimitedVisibleOutside])

    const steps = isCreatorAccommodation ? CREATOR_ACCOMMODATION_STEPS : STEPS
    const areStepsFulfilled = areAllTicketsCompleted

    const areTicketsBundlesInCart = !!ticketsBundles.length

    const isMarchPromoApplicable =
        getPromotionByName(PROMO_NAME_MARCH, 'isActiveForTickets') &&
        cartTickets.some(
            ({ detail, itemsBundle }) =>
                // promotion:
                // - is valid for tickets bought on march
                // - do not applies to tickets for infants
                // - do not applies to bundled tickets, e.g. senior package
                isDateInRange(detail.date, '2025-03-01', '2025-03-31') &&
                detail.person !== 'kid_to_90_cm' &&
                !itemsBundle?.length
        )

    const isCityBreakTicketsInCart = ticketsCityBreak.length > 0

    const handleAddTicketBox = () => {
        if (areTicketsUpdating) return
        dispatch(addPerson())
        calculateScroll({
            trigerringSection: 'tickets',
            trigerringAction: 'add',
        })
    }

    const skipToNextStep = () =>
        navigateToStep({ stepId: steps[stepIndex + 1].id })

    useEffect(() => {
        const shouldSkipBeDisabled =
            !areStepsFulfilled && (isCartEmpty || isCartFetching)

        setIsSkipButtonDisabled(shouldSkipBeDisabled)
    }, [isCartEmpty])

    useEffect(() => {
        if (!areAllTicketsCompleted) return
        animatePendingScroll('tickets-continue')
    }, [scrollIndex, areAllTicketsCompleted])

    // < analytics
    useEffect(() => {
        if (
            isCreatorAccommodation &&
            isAssignGuestsVisible &&
            accommodation &&
            accommodationDetails
        ) {
            if (!analyticsRef.current) {
                const accommodationAddToCartEvent =
                    calculateAccommodationAddToCartEvent(accommodation)
                pushDataLayer(accommodationAddToCartEvent)
                analyticsRef.current = true
            }
        }
    }, [isAssignGuestsVisible, accommodation, accommodationDetails])
    // analytics >

    useEffect(() => {
        removeNextStepsFromCart({ activeStepId: 'tickets' })

        const areTicketsInCart = cartTickets && cartTickets.length > 0
        const areTicketBoxesFilled =
            ticketBoxes.length > 1 && ticketBoxes[0].date.length > 0

        if (areTicketsInCart && !areTicketBoxesFilled) {
            const dates = sumTicketsDates(cartTickets)

            dispatch(setTicketsVisible(true))
            dispatch(setDates(dates))
            dispatch(setMultipleDays(dates.length > 1))

            const allCartItemBoxIds = cartTickets.map(
                (ticket) => ticket.detail.boxId
            )
            const uniqueBoxIds = Array.from(new Set(allCartItemBoxIds))

            if (uniqueBoxIds.length > 0) {
                dispatch(removePerson())
            }

            uniqueBoxIds.forEach((boxId) => {
                dispatch(addPerson(boxId))
            })

            cartTickets.forEach((ticket) => {
                dispatch(
                    addTicket({
                        data: ticket,
                        boxId: ticket.detail.boxId,
                    })
                )
            })
        }

        if (areTicketsBundlesInCart) {
            const dates = sumTicketsBundlesDates(ticketsBundles)
            if (ticketsBundles.length !== dates.length)
                dispatch(setIsErrorModalOpened(true))

            if (!areTicketsInCart) {
                dispatch(setTicketsVisible(true))
                dispatch(setDates(dates))
                dispatch(setMultipleDays(dates.length > 1))
                if (ticketBoxes.length && ticketBoxes[0].name === '') {
                    dispatch(removePerson())
                }
            }

            dispatch(setFamilyPackageVisibility(true))
        }

        // set data prom search params
        if (!areTicketsInCart && !areTicketsBundlesInCart) {
            const isMultipleDay = searchParams.get('singleDay') === 'false'
            if (isMultipleDay) {
                dispatch(setMultipleDays(true))
            }

            const datesFromParam = searchParams.get('dateRange')?.split(' ')
            const startDate = datesFromParam?.[0]
            const endDate = datesFromParam?.[2]
            const formattedDates = []
            startDate && formattedDates.push(formatDateISO(startDate))
            endDate && formattedDates.push(formatDateISO(endDate))
            formattedDates.length > 0 && dispatch(setDates(formattedDates))

            const ticketNumberFromParams = searchParams.get('numberOfTickets')
            if (ticketNumberFromParams && +ticketNumberFromParams > 1) {
                Array.from({ length: +ticketNumberFromParams - 1 }).forEach(
                    () => dispatch(addPerson())
                )
            }
        }

        scrollToTop(1)

        // < analytics
        pushDataLayer({
            event: 'productPresented',
            ecommerce: {
                items: [
                    {
                        item_name: 'Ticket',
                        item_brand: 'PoP',
                    },
                ],
            },
        })
        // analytics >
    }, [])

    const ref = useRef(false)

    useEffect(() => {
        if (ticketBoxes.length && ticketBoxes[0].name !== '') {
            if (!ref.current) {
                if (areTicketsBundlesInCart) {
                    dispatch(
                        setFamilyPackageSelection({
                            isSelected: true,
                            shouldKeepTickets: true,
                            peopleCount:
                                ticketBoxes.length +
                                FAMILY_PACKAGE_PERSON_COUNT,
                        })
                    )
                } else {
                    dispatch(
                        setFamilyPackageSelection({
                            isSelected: false,
                            shouldKeepTickets: true,
                            peopleCount: ticketBoxes.length,
                        })
                    )
                }
                ref.current = true
            }
        }

        if (ticketBoxes.length === 0) {
            if (areTicketsBundlesInCart) {
                dispatch(
                    setFamilyPackageSelection({
                        isSelected: true,
                        shouldKeepTickets: true,
                        peopleCount: FAMILY_PACKAGE_PERSON_COUNT,
                    })
                )
            }
        }
    }, [ticketBoxes, ticketsBundles])

    return (
        <Step
            title={formatMessage({
                id: 'global.steps.caption_tickets',
                defaultMessage: 'Bilety',
            })}
            isButtonDisabled={isNextStepDisabled && !isCityBreakTicketsInCart}
            isButtonVisible={areTicketsVisible || isCityBreakTicketsInCart}
        >
            <>
                {isTablet && MobileBenefitsInfoBoxes}

                {isCityBreakApplicableForTickets && <CityBreakBox />}

                <FindTickets />

                {areTicketsVisible && (
                    <>
                        {areTicketsBundlesInCart && (
                            <>
                                <FamilyPackage data={ticketsBundles} />
                                <Text variant="heading1" pt={[16, 24]}>
                                    <FormattedMessage
                                        id="package.common.other_tickets_header"
                                        defaultMessage="Other tickets"
                                    />
                                </Text>
                            </>
                        )}
                        {((isFamilyCheckboxSelected &&
                            (areTicketsBundlesInCart ||
                                cartTickets.length > 0)) ||
                            !isFamilyCheckboxSelected) && (
                            <>
                                {ticketBoxes.map((box, index) => {
                                    const { boxId } = box
                                    return (
                                        <TicketConfiguration
                                            theme={
                                                isDarkThemeForTickets
                                                    ? getDarkModeByLanguage(
                                                          language
                                                      )
                                                    : undefined
                                            }
                                            key={boxId}
                                            index={index}
                                            boxId={boxId}
                                        />
                                    )
                                })}
                                <Box
                                    gap={[16, 24]}
                                    align="stretch"
                                    id="tickets-continue"
                                >
                                    {people < TICKETS_TO_PARK_LIMIT &&
                                        areAllTicketsCompleted && (
                                            <AddItem
                                                onClick={handleAddTicketBox}
                                                testId="ticket-button-add-another"
                                            >
                                                <FormattedMessage
                                                    id="global.btn.add_another_ticket"
                                                    defaultMessage="Dodaj kolejny bilet"
                                                />
                                            </AddItem>
                                        )}
                                    {isMarchPromoApplicable && (
                                        <GuestsDataProvider>
                                            <MarchPromoInfo />
                                        </GuestsDataProvider>
                                    )}
                                    {areAllTicketsCompleted && (
                                        <CheckOpeningHours />
                                    )}
                                </Box>
                            </>
                        )}
                    </>
                )}
            </>
            {isCreatorAccommodation &&
                !isSkipButtonDisabled &&
                !areStepsFulfilled &&
                !areTicketsVisible &&
                !isCityBreakTicketsInCart && (
                    <Box
                        direction="row"
                        justify="flex-end"
                        align="center"
                        mt={24}
                    >
                        <Button
                            variant="borderPrimary"
                            onClick={skipToNextStep}
                            testId="step-button-continue-without-tickets"
                        >
                            <FormattedMessage
                                id="global.btn.skip_tickets"
                                defaultMessage="Kontynuuj bez biletów"
                            />
                        </Button>
                    </Box>
                )}
        </Step>
    )
}

export default Tickets
